import React, { useState, useMemo } from "react";
import { useStatementStore } from "../store/statementStore";

/**
 * FreeTextEditor — allows editing any text span in the PDF
 * (account name, address, bank name, headers, etc.)
 * while preserving exact font, size, color and position.
 */
export default function FreeTextEditor() {
    const { statement, stageFreeTextEdit, flushEdits, pendingFreeTextEdits } = useStatementStore();
    const [search, setSearch] = useState("");
    const [pageFilter, setPageFilter] = useState("all");

    // if (!statement) return null;

    // const spans = statement.free_text_spans || [];
    // const pages = [...new Set(spans.map((s) => s.page))].sort((a, b) => a - b);

    // const filtered = useMemo(() => {
    //     return spans.filter((s) => {
    //         const matchSearch = search === "" ||
    //             s.original_text.toLowerCase().includes(search.toLowerCase()) ||
    //             (s.new_text || "").toLowerCase().includes(search.toLowerCase());
    //         const matchPage = pageFilter === "all" || String(s.page) === String(pageFilter);
    //         return matchSearch && matchPage;
    //     });
    // }, [spans, search, pageFilter]);
    const spans = useMemo(() => statement?.free_text_spans ?? [], [statement]);
    const pages = [...new Set(spans.map((s) => s.page))].sort((a, b) => a - b);

    const filtered = useMemo(() => {
        return spans.filter((s) => {
            const matchSearch =
                search === "" ||
                s.original_text.toLowerCase().includes(search.toLowerCase()) ||
                (s.new_text || "").toLowerCase().includes(search.toLowerCase());

            const matchPage =
                pageFilter === "all" ||
                String(s.page) === String(pageFilter);

            return matchSearch && matchPage;
        });
    }, [spans, search, pageFilter]);

    if (!statement) return null;

    const editedCount = spans.filter((s) => s.is_edited || pendingFreeTextEdits[s.id] !== undefined).length;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-1">
                    Text Editor
                </div>
                <p className="text-[12px] text-zinc-400 leading-relaxed">
                    Edit any text in the PDF — names, addresses, account numbers, headers.
                    <span className="text-violet-400"> Font &amp; size preserved exactly.</span>
                </p>
            </div>

            {editedCount > 0 && (
                <div className="px-3 py-2 rounded border border-violet-500/30 bg-violet-500/[0.06] text-[11px] font-mono text-violet-300">
                    {editedCount} text span{editedCount !== 1 ? "s" : ""} edited
                </div>
            )}

            {/* Filters */}
            <div className="flex gap-2">
                <input
                    className="flex-1 bg-black/30 border border-white/10 rounded px-3 py-1.5 text-[12px] font-mono text-zinc-200 placeholder-zinc-600 outline-none focus:border-violet-500/50"
                    placeholder="Search text…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select
                    className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-[12px] font-mono text-zinc-200 outline-none focus:border-violet-500/50"
                    value={pageFilter}
                    onChange={(e) => setPageFilter(e.target.value)}
                >
                    <option value="all">All pages</option>
                    {pages.map((p) => (
                        <option key={p} value={String(p)}>
                            Page {p + 1}
                        </option>
                    ))}
                </select>
            </div>

            {/* Span list */}
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                {filtered.length === 0 && (
                    <div className="text-[12px] font-mono text-zinc-600 py-6 text-center">
                        No text spans found.
                    </div>
                )}
                {filtered.map((span) => (
                    <FreeTextRow
                        key={span.id}
                        span={span}
                        pending={pendingFreeTextEdits[span.id]}
                        onStage={(newText) => stageFreeTextEdit(span.id, newText)}
                        onCommit={flushEdits}
                    />
                ))}
            </div>

            {filtered.length > 0 && (
                <div className="text-[10px] font-mono text-zinc-600">
                    Showing {filtered.length} of {spans.length} spans
                </div>
            )}
        </div>
    );
}

function FreeTextRow({ span, pending, onStage, onCommit }) {
    const currentText =
        pending !== undefined
            ? pending
            : span.new_text !== null && span.new_text !== undefined
                ? span.new_text
                : span.original_text;

    const [draft, setDraft] = useState(currentText);
    const [focused, setFocused] = useState(false);

    React.useEffect(() => {
        if (!focused) {
            setDraft(
                pending !== undefined
                    ? pending
                    : span.new_text !== null && span.new_text !== undefined
                        ? span.new_text
                        : span.original_text
            );
        }
    }, [span, pending, focused]);

    const isEdited = span.is_edited || (pending !== undefined && pending !== span.original_text);
    const changed = draft !== span.original_text;

    function handleChange(e) {
        setDraft(e.target.value);
        onStage(e.target.value === span.original_text ? null : e.target.value);
    }

    async function handleBlur() {
        setFocused(false);
        if (changed) await onCommit();
    }

    function handleReset() {
        setDraft(span.original_text);
        onStage(null);
        onCommit();
    }

    return (
        <div
            className={`rounded-lg border px-3 py-2.5 transition-colors ${isEdited
                    ? "border-violet-500/40 bg-violet-500/[0.04]"
                    : "border-white/[0.06] bg-black/20 hover:border-white/10"
                }`}
        >
            {/* Meta row */}
            <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                    <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-600">
                        Pg {span.page + 1}
                    </span>
                    <span className="text-[9px] font-mono text-zinc-700">
                        {span.font?.split("+").pop()?.slice(0, 18)} · {span.size?.toFixed(1)}pt
                    </span>
                </div>
                {isEdited && (
                    <button
                        onClick={handleReset}
                        className="text-[9px] font-mono text-zinc-500 hover:text-zinc-300 transition-colors"
                        title="Reset to original"
                    >
                        ↺ reset
                    </button>
                )}
            </div>

            {/* Original text (dimmed) */}
            {isEdited && (
                <div className="text-[10px] font-mono text-zinc-600 mb-1 line-through truncate">
                    {span.original_text}
                </div>
            )}

            {/* Editable input */}
            <input
                className={`w-full bg-transparent border rounded px-2 py-1 text-[12px] font-mono outline-none transition-all ${focused
                        ? "border-violet-500/60 bg-violet-500/[0.05] text-zinc-100"
                        : isEdited
                            ? "border-violet-500/30 text-violet-300"
                            : "border-transparent text-zinc-300 hover:border-white/08"
                    }`}
                value={draft}
                onChange={handleChange}
                onFocus={() => setFocused(true)}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                    if (e.key === "Enter") e.target.blur();
                    if (e.key === "Escape") {
                        setDraft(span.original_text);
                        onStage(null);
                        setFocused(false);
                        e.target.blur();
                    }
                }}
            />
        </div>
    );
}
